/**
 * booqly-api HTTP client.
 *
 * Type-safe wrapper around the OpenAPI spec generated by `npm run gen:api`
 * (lands in src/api/booqly-api.d.ts). Adds bearer auth from the auth store
 * and a single retry on 401 via /auth/refresh — falling back to a hard
 * sign-out + redirect to the auth subapp when the refresh also fails.
 *
 * Raw `fetch()` calls that don't go through openapi-fetch should use
 * `authFetch` from `./auth-fetch` instead — same 401 → refresh → bounce
 * pipeline so a session-expired UX is consistent across every code path.
 */
import createClient from "openapi-fetch";
import type { paths } from "@/api/booqly-api";
import { useAuthStore } from "./auth";
import { sessionBus } from "./session-bus";
import { toApiError } from "./api-error";

const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8081";
const authUrl = import.meta.env.VITE_AUTH_APP_URL ?? "http://localhost:5174";

const RETURN_TO_KEY = "booqly-app.return-to";

// Module-level guard so a burst of parallel 401s from different react-query
// subscriptions only triggers one redirect (and one toast), not N.
let signOutInFlight = false;

/**
 * Endpoints under `/auth/*` that don't take a Bearer token. Mirrors
 * `auth-fetch.ts` — kept in sync with that file. Authenticated
 * `/auth/*` endpoints (e.g. `/auth/email/verify/send`) intentionally
 * fall through to the refresh-and-retry path on 401.
 */
function isBootstrapAuthEndpoint(url: string): boolean {
  return (
    url.includes("/auth/refresh") ||
    url.includes("/auth/sign-in") ||
    url.includes("/auth/sign-out") ||
    url.includes("/auth/forgot") ||
    url.includes("/auth/reset")
  );
}

/**
 * Stash the path so bootstrap can land the user back here after sign-in,
 * emit a "session expired" event so the Toaster shows feedback, then redirect
 * after a short delay so the toast is actually readable. Exported so the
 * hand-rolled `authFetch` in `./auth-fetch` shares the same bouncer.
 */
export function bounceToSignIn() {
  if (signOutInFlight) return;
  signOutInFlight = true;

  // Save the page the user was on so bootstrap can replay the navigation
  // after a successful re-sign-in. Skip empty / sign-in paths to avoid loops.
  try {
    const here = window.location.pathname + window.location.search;
    if (here && here !== "/" && !here.startsWith("/auth")) {
      localStorage.setItem(RETURN_TO_KEY, here);
    }
  } catch {
    // localStorage may be disabled (private mode etc.) — non-fatal.
  }

  useAuthStore.getState().clear();
  sessionBus.emit("expired");

  // ~900ms gives the toast time to render + read before the navigation
  // unloads it. window.location.assign rather than .replace so the back
  // button still gets the user out of the auth flow if needed.
  setTimeout(() => window.location.assign(`${authUrl}/sign-in`), 900);
}

// credentials:"include" so the httpOnly refresh cookie (path=/api/v1/auth) is
// sent to, and Set-Cookie stored from, the auth endpoints (refresh, workspace
// switch/create). It's path-scoped, so non-auth requests never carry it.
export const api = createClient<paths>({ baseUrl, credentials: "include" });

api.use({
  onRequest({ request }) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status === 401 && !isBootstrapAuthEndpoint(request.url)) {
      const refreshed = await useAuthStore.getState().tryRefresh();
      if (refreshed) {
        const retry = new Request(request, {
          headers: {
            ...Object.fromEntries(request.headers),
            Authorization: `Bearer ${refreshed}`,
          },
        });
        return fetch(retry);
      }
      // Refresh failed → session expired. Clear local auth + bounce to sign in
      // so the user doesn't see a cryptic "data is undefined" from react-query.
      bounceToSignIn();
    }
    // Other errors (incl. the email-verified 403) carry a machine `code`; the caller's
    // error handling turns it into a specific message via unwrap()/ApiError, so there's
    // no special-casing here (and no duplicate toast).
    return response;
  },
});

/**
 * Unwrap an `openapi-fetch` `{data, error}` tuple into the data, throwing on
 * either an explicit error or an empty body. Use inside react-query `queryFn`s
 * so a missing payload never silently propagates as `undefined` (which makes
 * react-query throw the unhelpful "Query data cannot be undefined" message).
 *
 *   const data = unwrap(await api.GET("/api/v1/dashboard/overview", {}));
 */
export function unwrap<T>(res: { data?: T; error?: unknown }): T {
  if (res.error) {
    // Throw a humanized ApiError: its `.message` is a specific, user-facing sentence,
    // so every `(e as Error).message` render/toast site shows the real reason for free.
    throw toApiError(res.error);
  }
  if (res.data === undefined || res.data === null) {
    throw new Error("Empty response from server.");
  }
  return res.data;
}
